import { capitalize } from "lodash";
import { capitalizeWords } from "./capitalizeCamelCase.pipe";
import { Length, Mass } from '@/../types/enums';
import convert from "convert-units";

export function isValidUUID(uuid: string): boolean {
    if( isEmpty(uuid) ) return false;
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(uuid);
}

export function isEmpty(data: any): boolean {
    if( !data ) return true;
    if( Array.isArray(data) ) return isEmptyArray(data);
    if( typeof(data) === 'undefined' ) return true;
    if( typeof(data) === 'number' ) return isEmptyNumber(data as number);
    if( typeof(data) === 'string' ) return isEmptyString(data as string);

    return false;
}
export function isNotEmpty(data: any): boolean {
    return !isEmpty(data);
}

export function isEmptyString(s: string): boolean {
    if( !s ) return true;
    return s.trim().length === 0;
}
export function isEmptyNumber(n: number): boolean {
    if( !n ) return true;
    return isZero(n);
}

export function isEmptyArray(a: Array<any>): boolean {
    if( !a ) return true;
    return a.length === 0
}
export function isNotEmptyArray(a: Array<any>): boolean {
    return !isEmptyArray(a);
}

export function isZero(n: number): boolean {
    if( !n ) return true;
    return n === 0;
}
export function isNotZero(n: number): boolean {
    return !isZero(n);
}

export function fullName(given_name: string, family_name: string): string {
    if( isNotEmpty(family_name) ) {
        if( isNotEmpty(given_name) ) return `${capitalize(given_name)} ${capitalizeWords(family_name)}`
        return capitalizeWords(family_name);
    } else if( isNotEmpty(given_name) ) {
        return capitalize(given_name)
    }
    return '';
}

export function heightInFt(height: number, heightUnit: Length = Length.CENTIMETER): string {
    if( isEmpty(height) ) return '';
    return `${~~convert(height).from(heightUnit).to("ft")}' ${~~convert(height).from(heightUnit).to("in") % 12}"`;
}
export function heightInCm(height: number, heightUnit: Length = Length.CENTIMETER): string {
    if( isEmpty(height) ) return '';
    return `${~~convert(height).from(heightUnit).to("cm").toFixed(0)} cm`;
}
export function weightInLbs(weight: number, weightUnit: Mass = Mass.KILOGRAM): string {
    if( isEmpty(weight) ) return '';
    return `${~~convert(weight).from(weightUnit).to("lb").toFixed(0)} lbs`;
}
export function weightInKg(weight: number, weightUnit: Mass = Mass.KILOGRAM): string {
    if( isEmpty(weight) ) return '';
    return `${~~convert(weight).from(weightUnit).to("kg").toFixed(0)} kg`;
}
export function heightStr(height: number, heightUnit: Length = Length.CENTIMETER, output: Length = undefined): string {
    if( isEmpty(height) ) return '';

    if( output === Length.CENTIMETER ) return `${ heightInCm(height, heightUnit) }`;
    if( output === Length.INCH ) return `${ heightInFt(height, heightUnit) }`;
    return `${ heightInFt(height, heightUnit) } / ${ heightInCm(height, heightUnit) }`;
}
export function weightStr(weight: number, weightUnit: Mass = Mass.KILOGRAM, output: Mass = undefined): string {
    if( isEmpty(weight) ) return '';

    if( output === Mass.KILOGRAM ) return `${ weightInKg(weight, weightUnit) }`;
    if( output === Mass.POUND ) return `${ weightInLbs(weight, weightUnit) }`;
    return `${ weightInLbs(weight, weightUnit) } / ${ weightInKg(weight, weightUnit) }`;
}
export function heightStringToInches(heightString: string): number {
    if( isEmpty(heightString) ) return undefined;

    const trimmedHeightString = heightString.trim();

    // --- 1. Handle "X feet Y inches" or "X ft Y in" format ---
    const fullSentenceMatch = trimmedHeightString.match(
        /^(\d+)\s*(?:feet|ft)['"]?\s*(\d+)\s*(?:inches|in)['"]?$/i
    );
    if (fullSentenceMatch) {
        const feet = parseInt(fullSentenceMatch[1], 10);
        const inches = parseInt(fullSentenceMatch[2], 10);
        if (!isNaN(feet) && !isNaN(inches)) {
        return (feet * 12) + inches;
        }
    }

    // --- 2. Handle "XftY" format (e.g., 5ft2, 6ft10) ---
    const compactFtInchesMatch = trimmedHeightString.match(
        /^(\d+)\s*(?:ft|feet)(\d+)$/i
    );
    if (compactFtInchesMatch) {
        const feet = parseInt(compactFtInchesMatch[1], 10);
        const inches = parseInt(compactFtInchesMatch[2], 10);
        if (!isNaN(feet) && !isNaN(inches)) {
        return (feet * 12) + inches;
        }
    }

    // --- 3. Handle "X feet" or "X ft" or "X'" format (no inches specified) ---
    const feetOnlyMatch = trimmedHeightString.match(
        /^(\d+)\s*(?:feet|ft|['’])?$/i
    );
    if (feetOnlyMatch) {
        const feet = parseInt(feetOnlyMatch[1], 10);
        if (!isNaN(feet)) {
        return feet * 12;
        }
    }

    // --- 4. Handle "X inches" or "X in" or single number "X" (total inches) ---
    const inchesOnlyMatch = trimmedHeightString.match(
        /^(\d+)\s*(?:inches|in|["”])?$/i
    );
    if (inchesOnlyMatch) {
        const inches = parseInt(inchesOnlyMatch[1], 10);
        if (!isNaN(inches)) {
        return inches;
        }
    }

    // --- 5. Handle "X Y" format (e.g., 5 6, 6 10) ---
    const spaceSeparatedFeetInchesMatch = trimmedHeightString.match(
        /^(\d+)\s+(\d+)$/
    );
    if (spaceSeparatedFeetInchesMatch) {
        const feet = parseInt(spaceSeparatedFeetInchesMatch[1], 10);
        const inches = parseInt(spaceSeparatedFeetInchesMatch[2], 10);
        if (!isNaN(feet) && !isNaN(inches) && inches < 12) {
        return (feet * 12) + inches;
        }
    }

    // --- 6. Handle "X,Y" format (e.g., 5,2, 6,10) ---
    const commaSeparatedFormatMatch = trimmedHeightString.match(/^(\d+),(\d+)$/);
    if (commaSeparatedFormatMatch) {
        const feet = parseInt(commaSeparatedFormatMatch[1], 10);
        const inches = parseInt(commaSeparatedFormatMatch[2], 10);
        if (!isNaN(feet) && !isNaN(inches) && inches < 12) {
        return (feet * 12) + inches;
        }
    }

    // --- 7. Handle "X/Y" format (e.g., 5/4, 6/10) ---
    // This assumes X is feet and Y is inches, separated by a forward slash.
    const slashSeparatedFormatMatch = trimmedHeightString.match(/^(\d+)\/(\d+)$/);
    if (slashSeparatedFormatMatch) {
        const feet = parseInt(slashSeparatedFormatMatch[1], 10);
        const inches = parseInt(slashSeparatedFormatMatch[2], 10);
        // As with X.Y, X-Y, and X,Y, typically Y < 12 for this format's semantic correctness.
        if (!isNaN(feet) && !isNaN(inches) && inches < 12) {
        return (feet * 12) + inches;
        }
    }

    // --- 8. Handle "X-Y" format (e.g., 5-10, 6-2) ---
    const hyphenatedFormatMatch = trimmedHeightString.match(/^(\d+)-(\d+)$/);
    if (hyphenatedFormatMatch) {
        const feet = parseInt(hyphenatedFormatMatch[1], 10);
        const inches = parseInt(hyphenatedFormatMatch[2], 10);
        if (!isNaN(feet) && !isNaN(inches) && inches < 12) {
        return (feet * 12) + inches;
        }
    }

    // --- 9. Handle "X'Y"" or "X’Y”" format (compact notation with symbols) ---
    const compactSymbolFeetInchesMatch = trimmedHeightString.match(
        /^(\d+)['’](\d+)["”]?$/
    );
    if (compactSymbolFeetInchesMatch) {
        const feet = parseInt(compactSymbolFeetInchesMatch[1], 10);
        const inches = parseInt(compactSymbolFeetInchesMatch[2], 10);
        if (!isNaN(feet) && !isNaN(inches)) {
        return (feet * 12) + inches;
        }
    }

    // --- 10. Handle "X.Y" format (e.g., 5.11, 6.2 - where .Y is inches) ---
    const decimalFormatMatch = trimmedHeightString.match(/^(\d+)\.(\d+)$/);
    if (decimalFormatMatch) {
        const feet = parseInt(decimalFormatMatch[1], 10);
        const inches = parseInt(decimalFormatMatch[2], 10);
        if (!isNaN(feet) && !isNaN(inches) && inches < 12) {
        return (feet * 12) + inches;
        }
    }

    // --- 11. Handle single number (e.g., "71" for 71 inches directly) ---
    const singleNumberMatch = trimmedHeightString.match(/^(\d+)$/);
    if (singleNumberMatch) {
        const totalInches = parseInt(singleNumberMatch[1], 10);
        if (!isNaN(totalInches)) {
            return totalInches;
        }
    }
    
    return undefined;
}

/**
 * Checks if a string is a structurally valid web link (URL).
 *
 * This regex is highly permissive to allow common patterns, including:
 * - Must start with http:// or https://
 * - Followed by a domain name (with dots and optional subdomains)
 * - May include paths, query parameters, and fragments
 *
 * @param url The string to validate.
 * @returns True if the string is a valid web link structure, otherwise false.
 */
export function isValidUrl(url: string): boolean {
    // Regular Expression for URL validation (Wider match for common use cases)
    // ^(https?:\/\/) ensures http:// or https:// at the start.
    // ([\da-z\.-]+) matches domain name parts (letters, digits, dots, hyphens).
    // \.([a-z\.]{2,6}) matches the TLD (e.g., .com, .co.uk).
    // ([\/\w \.-]*)*\/?$ matches optional path, query, and fragment.
    const urlRegex: RegExp = /^(https?:\/\/)([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/i;

    if (!url) {
        return false;
    }

    // Trim the input and test against the regex
    return urlRegex.test(url.trim());
}
